/**
 * Still frames of a recording, pulled out in the browser for the editor's
 * filmstrip: a hidden video element per source is seeked to each wanted
 * moment and the picture copied off it, cropped to the tile's 4:3.
 *
 * Seeking is one moment at a time per source, so each asker (a piece on the
 * timeline) hands over a list that replaces its last one: the tiles on
 * screen right now come first, and the ones a zoom has already moved past
 * are never fetched.
 */

/** A tile is 4:3; frames are kept a little larger than they draw, for sharp screens. */
export const FRAME_W = 160;
export const FRAME_H = 120;
/** Moments are rounded to this, so a tile a hair to the left reuses a frame. */
const QUANT = 0.25;
const CACHE_MAX = 320;
const SEEK_TIMEOUT_MS = 6000;

interface Source {
  el: HTMLVideoElement;
  ready: Promise<boolean>;
  busy: boolean;
  /** Moments still to pull, by who asked. */
  wanted: Map<string, number[]>;
  dead: boolean;
}

function keyOf(source: string, time: number): string {
  return `${source}@${time.toFixed(2)}`;
}

export class FrameStore {
  private sources = new Map<string, Source>();
  private frames = new Map<string, ImageBitmap>();
  private listeners = new Set<() => void>();
  private disposed = false;

  addSource(key: string, url: string): void {
    if (this.sources.has(key) || this.disposed) return;
    const el = document.createElement("video");
    el.crossOrigin = "anonymous";
    el.muted = true;
    el.playsInline = true;
    el.preload = "auto";
    const ready = new Promise<boolean>((resolve) => {
      el.addEventListener("loadedmetadata", () => resolve(true), {
        once: true,
      });
      el.addEventListener("error", () => resolve(false), { once: true });
    });
    el.src = url;
    this.sources.set(key, { el, ready, busy: false, wanted: new Map(), dead: false });
  }

  has(key: string): boolean {
    return this.sources.has(key);
  }

  quantize(time: number): number {
    return Math.max(0, Math.round(time / QUANT) * QUANT);
  }

  /** The frame at a moment, if it has been pulled already. */
  frame(source: string, time: number): ImageBitmap | null {
    const key = keyOf(source, this.quantize(time));
    const bitmap = this.frames.get(key) ?? null;
    if (bitmap) {
      // Recently drawn frames stay: a re-insert puts this one at the back of the line.
      this.frames.delete(key);
      this.frames.set(key, bitmap);
    }
    return bitmap;
  }

  /** The moments one asker wants of a source now, in the order to pull them, replacing its earlier list. */
  want(source: string, asker: string, times: number[]): void {
    const one = this.sources.get(source);
    if (!one || one.dead) return;
    const seen = new Set<string>();
    const list: number[] = [];
    for (const time of times) {
      const q = this.quantize(time);
      const key = keyOf(source, q);
      if (seen.has(key) || this.frames.has(key)) continue;
      seen.add(key);
      list.push(q);
    }
    if (list.length) one.wanted.set(asker, list);
    else one.wanted.delete(asker);
    if (one.wanted.size) void this.pump(source, one);
  }

  /** An asker that has gone: nothing more is pulled for it. */
  forget(asker: string): void {
    for (const one of this.sources.values()) one.wanted.delete(asker);
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  dispose(): void {
    this.disposed = true;
    for (const one of this.sources.values()) {
      one.wanted.clear();
      one.el.removeAttribute("src");
      one.el.load();
    }
    for (const bitmap of this.frames.values()) bitmap.close();
    this.frames.clear();
    this.sources.clear();
    this.listeners.clear();
  }

  private async pump(key: string, one: Source): Promise<void> {
    if (one.busy) return;
    one.busy = true;
    try {
      if (!(await one.ready)) {
        one.dead = true;
        return;
      }
      const canvas = document.createElement("canvas");
      canvas.width = FRAME_W;
      canvas.height = FRAME_H;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      for (;;) {
        // Askers take turns, so no piece waits on another's whole strip.
        const time = nextWanted(one.wanted);
        if (time === null || this.disposed) break;
        const frameKey = keyOf(key, time);
        if (this.frames.has(frameKey)) continue;
        const grabbed = await seekTo(
          one.el,
          Math.min(time, Math.max(0, one.el.duration - 0.05)),
        );
        if (!grabbed) continue;
        const { videoWidth: w, videoHeight: h } = one.el;
        if (!w || !h) continue;
        // Cover-crop the picture to 4:3 from its centre.
        let sw = w;
        let sh = h;
        if (w / h > FRAME_W / FRAME_H) sw = (h * FRAME_W) / FRAME_H;
        else sh = (w * FRAME_H) / FRAME_W;
        ctx.drawImage(
          one.el,
          (w - sw) / 2,
          (h - sh) / 2,
          sw,
          sh,
          0,
          0,
          FRAME_W,
          FRAME_H,
        );
        try {
          const bitmap = await createImageBitmap(canvas);
          this.remember(frameKey, bitmap);
        } catch {
          // A tainted canvas (no CORS on the media): no frames from this source.
          one.dead = true;
          one.wanted.clear();
          return;
        }
        for (const listener of this.listeners) listener();
      }
    } finally {
      one.busy = false;
    }
  }

  private remember(key: string, bitmap: ImageBitmap): void {
    this.frames.set(key, bitmap);
    while (this.frames.size > CACHE_MAX) {
      const oldest = this.frames.keys().next().value as string;
      this.frames.get(oldest)?.close();
      this.frames.delete(oldest);
    }
  }
}

function nextWanted(wanted: Map<string, number[]>): number | null {
  for (const [asker, list] of wanted) {
    const time = list.shift();
    // Move this asker to the back of the line, or out of it when it is done.
    wanted.delete(asker);
    if (list.length) wanted.set(asker, list);
    if (time !== undefined) return time;
  }
  return null;
}

function seekTo(el: HTMLVideoElement, time: number): Promise<boolean> {
  return new Promise((resolve) => {
    if (Math.abs(el.currentTime - time) < 0.01 && el.readyState >= 2) {
      resolve(true);
      return;
    }
    let done = false;
    const finish = (ok: boolean) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      el.removeEventListener("seeked", onSeeked);
      el.removeEventListener("error", onError);
      resolve(ok);
    };
    const onSeeked = () => finish(el.readyState >= 2);
    const onError = () => finish(false);
    const timer = setTimeout(() => finish(false), SEEK_TIMEOUT_MS);
    el.addEventListener("seeked", onSeeked);
    el.addEventListener("error", onError);
    el.currentTime = time;
  });
}
